import {
  getSentryEventMessage,
  isAutomatedScannerMessage,
  isAutomatedScannerUserAgent,
} from "./sentryEventFilters";

type SentryTagMap = Record<string, string>;

/**
 * Normalize Sentry Event Tags.
 */
export function normalizeSentryEventTags(
  tags: Record<string, string | number | boolean | null | undefined> | undefined
): SentryTagMap {
  const normalized: SentryTagMap = {};

  for (const [key, value] of Object.entries(tags ?? {})) {
    if (value !== undefined && value !== null) {
      normalized[key] = String(value);
    }
  }

  return normalized;
}

/**
 * Resolve Authenticated Traffic Class.
 */
export function resolveAuthenticatedTrafficClass(
  tags: SentryTagMap,
  hasAuthenticatedUser: boolean
): SentryTagMap {
  const nextTags = { ...tags };
  nextTags.has_authenticated_user = hasAuthenticatedUser ? "true" : "false";

  if (!nextTags.traffic_class) {
    nextTags.traffic_class = hasAuthenticatedUser ? "authenticated" : "anonymous";
  }

  return nextTags;
}

export function applyScannerMessageTags(tags: SentryTagMap, message: string): SentryTagMap {
  if (!isAutomatedScannerMessage(message)) {
    return tags;
  }

  return {
    ...tags,
    traffic_class: "automated_scanner",
    scanner_pattern: "cefsharp_bridge",
  };
}

/**
 * Resolve Event User Agent.
 */
export function resolveEventUserAgent(event: {
  request?: { headers?: Record<string, string> };
  contexts?: Record<string, unknown>;
}): string {
  const headers = event.request?.headers;
  const headerUserAgent =
    headers && typeof headers["User-Agent"] === "string" ? headers["User-Agent"] : "";
  const browserContext = event.contexts?.browser as
    | { user_agent?: string; name?: string }
    | undefined;
  const browserUserAgent =
    typeof browserContext?.user_agent === "string" ? browserContext.user_agent : "";
  const browserName = typeof browserContext?.name === "string" ? browserContext.name : "";

  return headerUserAgent || browserUserAgent || browserName;
}

export function applyScannerUserAgentTags(tags: SentryTagMap, userAgent: string): SentryTagMap {
  if (!userAgent || !isAutomatedScannerUserAgent(userAgent)) {
    return tags;
  }

  return {
    ...tags,
    traffic_class: "automated_scanner",
    scanner_user_agent: "true",
  };
}

/**
 * Build Traffic Context Tags.
 */
export function buildTrafficContextTags<
  T extends {
    tags?: Record<string, string | number | boolean | null | undefined>;
    user?: { id?: string | number };
    contexts?: Record<string, unknown>;
    request?: { headers?: Record<string, string> };
  },
>(event: T, hint?: { originalException?: unknown }): SentryTagMap {
  const message = getSentryEventMessage(event, hint);
  const hasAuthenticatedUser = Boolean(event.user?.id);

  let tags = normalizeSentryEventTags(event.tags);
  tags = resolveAuthenticatedTrafficClass(tags, hasAuthenticatedUser);
  tags = applyScannerMessageTags(tags, message);

  const userAgent = resolveEventUserAgent(event);
  tags = applyScannerUserAgentTags(tags, userAgent);

  return tags;
}
